import { AppField, Filter, LookupFieldValue, SimpleRecord, Tag } from "@smartsuite/types";
import { useMemo } from "react";
import { useFormikContext } from "formik";
import { useQuery } from "react-query";
import {
  getInternalLinkedRecords,
  getInternalRelatedRecords,
  getLinkedRecords,
  getRelatedRecords,
} from "../../../../../api";
import { useFormContext } from "../../../../../hooks/useFormContext";
import { useIsPreview } from "../../../../../hooks/useIsPreview";
import { BaseRecord, FormConfigApplicationStructureItem } from "../../../../../types/form";
import { getDisplayValue } from "../../../linked-record/display-value/getFieldDisplayValue";
import { resolveDynamicFilterValues } from "../../../linked-record/LinkedRecordFieldControl/linked-records.helpers";

const LOOKUP_VALUE_SEPARATOR = " | ";
const MISSING_VALUE_PLACEHOLDER = "-";

export interface UseLookupValueParams {
  lookupField: FormConfigApplicationStructureItem;
  lrFieldSlug: string;
  linkedApplication: string;
  selectedIds: string[];
}

export interface UseLookupValueResult {
  displayValue: string | null;
  rawValue: LookupFieldValue | null;
  isLoading: boolean;
}

/**
 * Resolves a dot-notation path (e.g. "contact_name.first_name") from a plain record object.
 * Returns the leaf value, or null if any intermediate object is missing.
 */
function resolveFieldPath(record: Record<string, unknown>, path: string): unknown {
  let current: unknown = record;
  for (const key of path.split(".")) {
    if (current == null || typeof current !== "object") return null;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

/**
 * Builds the lookup field value for a set of linked record IDs from the related records array.
 * Each linked record maps to one group containing one value (or multiple for multi-value fields).
 */
export function buildLookupValueFromRelatedRecords(
  linkedRecordIds: string[],
  relatedRecords: SimpleRecord[],
  lookupField: AppField
): LookupFieldValue {
  const fieldSelection = lookupField.params?.field_selection;
  if (!fieldSelection) return [];

  return linkedRecordIds.map((linkedId) => {
    const linkedRecord = relatedRecords.find(
      (r) => r.id === linkedId || (r as { _id?: string })._id === linkedId
    );
    const value = linkedRecord
      ? resolveFieldPath(linkedRecord as Record<string, unknown>, fieldSelection)
      : null;
    return [value];
  });
}

/**
 * Converts the selected linked record IDs into a display string using getDisplayValue.
 * Missing values are represented by the MISSING_VALUE_PLACEHOLDER constant.
 */
function lookupValueToDisplayString(
  selectedIds: string[],
  lookupField: AppField,
  relatedRecords: SimpleRecord[],
  tags: Tag[],
  relatedRecordsForDisplay?: BaseRecord[]
): string {
  const targetFieldStructure = lookupField.params?.target_field_structure;
  if (!targetFieldStructure) return MISSING_VALUE_PLACEHOLDER;

  const fieldSelection = lookupField.params?.field_selection ?? "";
  const fieldsToShow = { [fieldSelection]: targetFieldStructure };
  const displayRelated = relatedRecordsForDisplay ?? relatedRecords;

  const parts = selectedIds.map((id) => {
    const record = relatedRecords.find((r) => r.id === id || (r as { _id?: string })._id === id);
    if (!record) return MISSING_VALUE_PLACEHOLDER;
    const val = getDisplayValue(record, fieldSelection, fieldsToShow, tags, displayRelated, true);
    return val != null && val !== "" ? val : MISSING_VALUE_PLACEHOLDER;
  });

  return parts.join(LOOKUP_VALUE_SEPARATOR);
}

/**
 * Extracts and resolves the field filter from lookup field params.
 * Returns null if filtering is disabled or no filter is configured.
 */
function useLookupFilter(lookupField: FormConfigApplicationStructureItem): Filter | null {
  const { values } = useFormikContext();
  const includeFilters = lookupField.params?.include_filters === true;
  const fieldFilter = lookupField.params?.field_filter;

  return useMemo(() => {
    if (!includeFilters || !fieldFilter) return null;

    const formValues = values as {
      [key: string]: Array<{ id: string }>;
    };
    return resolveDynamicFilterValues(fieldFilter, formValues);
  }, [includeFilters, JSON.stringify(fieldFilter), JSON.stringify(values)]);
}

export function useLookupValue({
  lookupField,
  lrFieldSlug,
  linkedApplication,
  selectedIds,
}: UseLookupValueParams): UseLookupValueResult {
  const formContext = useFormContext();
  const tags = formContext.config.tags ?? [];
  const { isInternal, reportId } = formContext;
  const { isPreview } = useIsPreview();
  const targetFieldStructure = lookupField.params?.target_field_structure;
  const targetSlug = lookupField.params?.field_selection;
  const filterData = useLookupFilter(lookupField);

  const isUserfieldLookup = targetFieldStructure?.field_type === "userfield";
  const isLinkedRecordLookup = targetFieldStructure?.field_type === "linkedrecordfield";
  const isDependencyRelationLookup = targetFieldStructure?.field_type === "dependencyrelationfield";
  const isTeamfieldLookup = targetFieldStructure?.field_type === "teamfield";
  const needsRelatedForDisplay =
    isUserfieldLookup || isLinkedRecordLookup || isDependencyRelationLookup || isTeamfieldLookup;

  // For a linkedrecordfield lookup, records need to be resolved against the *nested* linked app
  // (the app that the linkedrecordfield in Table 1 itself points to — i.e. Table 2), not the
  // main linkedApplication (Table 1). For userfield, teamfield and dependencyrelationfield,
  // references live inside the linked table, so the main linkedApplication is correct.
  const relatedForDisplayAppId = isLinkedRecordLookup
    ? (targetFieldStructure?.params as { linked_application?: string } | undefined)
        ?.linked_application
    : linkedApplication;

  const { data: records, isLoading } = useQuery(
    [
      "lookup_value",
      lookupField.slug,
      lrFieldSlug,
      linkedApplication,
      selectedIds.join(","),
      JSON.stringify(filterData),
    ],
    async () => {
      if (
        !formContext.accountId ||
        (!isInternal && !formContext.config.sharing_hash) ||
        !linkedApplication ||
        selectedIds.length === 0 ||
        !targetSlug
      ) {
        return [];
      }
      const apiFieldSlug =
        typeof targetSlug === "string" && targetSlug.includes(".")
          ? targetSlug.split(".")[0]
          : targetSlug;
      const fieldsInUse = new Set<string>(
        ["title", apiFieldSlug].filter((s): s is string => Boolean(s))
      );
      if (filterData) {
        filterData.fields.forEach((f) => fieldsInUse.add(f.field));
      }
      try {
        const res =
          isInternal && reportId
            ? await getInternalLinkedRecords(
                formContext.accountId,
                reportId,
                linkedApplication,
                lrFieldSlug,
                fieldsInUse,
                filterData,
                selectedIds
              )
            : await getLinkedRecords(
                formContext.accountId,
                formContext.config.sharing_hash,
                linkedApplication,
                lrFieldSlug,
                fieldsInUse,
                filterData,
                isPreview,
                formContext.passcode,
                selectedIds
              );
        return res ?? [];
      } catch {
        return [];
      }
    },
    {
      enabled: selectedIds.length > 0,
      staleTime: 60_000,
    }
  );

  const { data: relatedRecordsForDisplay, isLoading: isRelatedRecordsLoading } = useQuery(
    ["lookup_related_records", lookupField.slug, relatedForDisplayAppId, targetSlug],
    async () => {
      if (
        !formContext.accountId ||
        (!isInternal && !formContext.config.sharing_hash) ||
        !relatedForDisplayAppId ||
        !targetSlug ||
        !needsRelatedForDisplay
      ) {
        return undefined;
      }
      try {
        const result =
          isInternal && reportId
            ? await getInternalRelatedRecords({
                accountId: formContext.accountId,
                reportId,
                linkedAppId: relatedForDisplayAppId,
                visibleFields: [targetSlug],
              })
            : await getRelatedRecords({
                accountId: formContext.accountId,
                linkedAppId: relatedForDisplayAppId,
                hash: formContext.config.sharing_hash,
                isPreview,
                visibleFields: [targetSlug],
                passcode: formContext.passcode,
              });
        return result;
      } catch {
        return undefined;
      }
    },
    {
      enabled: selectedIds.length > 0 && needsRelatedForDisplay && !!relatedForDisplayAppId,
      staleTime: Infinity,
    }
  );

  // When a filter is active, only include IDs that survived filtering
  // (i.e. records the API actually returned). Without a filter, use all selectedIds.
  const effectiveIds = useMemo(() => {
    if (!filterData || !records) return selectedIds;
    const returnedIds = new Set(records.map((r) => r.id));
    return selectedIds.filter((id) => returnedIds.has(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, filterData, selectedIds.join(",")]);

  const lookupValue = useMemo(
    () =>
      records && records.length > 0 && targetFieldStructure
        ? buildLookupValueFromRelatedRecords(effectiveIds, records, lookupField as AppField)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, targetFieldStructure, effectiveIds.join(",")]
  );

  const relatedForDisplay = useMemo(
    () =>
      needsRelatedForDisplay && relatedRecordsForDisplay?.length
        ? relatedRecordsForDisplay.map((r) => ({
            ...r,
            title: r.title ?? (r as { name?: string }).name,
          }))
        : undefined,
    [needsRelatedForDisplay, relatedRecordsForDisplay]
  );

  const displayValue = useMemo(() => {
    if (effectiveIds.length === 0) return null;
    if (records == null) return MISSING_VALUE_PLACEHOLDER;
    return lookupValueToDisplayString(
      effectiveIds,
      lookupField as AppField,
      records,
      tags,
      relatedForDisplay
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, tags, relatedForDisplay, effectiveIds.join(",")]);

  return {
    displayValue,
    rawValue: lookupValue,
    isLoading: isLoading || (needsRelatedForDisplay && isRelatedRecordsLoading),
  };
}

export { LOOKUP_VALUE_SEPARATOR, MISSING_VALUE_PLACEHOLDER };
